<template>
  <div class="h-full">
    <BaseTable
      v-if="customers"
      :headers="tableHeaders"
      :list="filteredCustomers"
      :total-records="customersTotalCount"
      :page="customersPageNumber"
      :items-per-page="customersItemsPerPage"
      table-id="customers"
      clickable
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @delete-multiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      is-saving-layout-hidden>
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="w-4 h-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>
    <DCCustomerModalCreateEdit
      v-if="createEditModalActive && currentCustomer"
      :customer="currentCustomer"
      :mode="createEditModalMode"
      :view-only="createEditModalMode === 'view'"
      :representative-id="props.representativeId || ''"
      :existing-customers="customers"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import type { DCCustomer, RepresentativeFiltersData } from '@/types/DataCollection';
import BaseTable from '../common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import DCCustomerModalCreateEdit from '@/components/traders/DCCustomerModalCreateEdit.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';
import { deepCopy } from '@/utils/data';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getDCCustomersWithConditions } from '@/services/api/dataCollection';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId?: string;
}>();

const customers = defineModel<DCCustomer[]>('customers', { default: [] });
const isCustomerModified = defineModel<boolean>('isCustomerModified', { default: false });
const filterIsMapped = ref<boolean | undefined>(undefined);
const customersItemsPerPage = ref(50);
const customersPageNumber = ref(1);
const customersTotalCount = ref<number>(0);

// Modal state
const createEditModalActive = ref(false);
const currentCustomer = ref<DCCustomer>();
const createEditModalMode = ref<'add' | 'edit' | 'view'>('add');

const originalCustomers = ref<DCCustomer[]>([]);

const tableHeaders = [
  {
    value: 'customer.trader.name',
    text: $t('baseObject.name'),
    type: 'text',
    active: true,
  },
  {
    value: 'customer.code',
    text: $t('baseObject.code'),
    type: 'text',
    active: true,
  },
  {
    value: 'customerRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'isActive',
    text: $t('baseObject.isActive'),
    type: 'check',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const filteredCustomers = computed<DCCustomer[]>(() => {
  if (!customers.value) return [];
  let list = customers.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

watch(
  () => customers.value,
  (newData) => {
    isCustomerModified.value = JSON.stringify(newData) !== JSON.stringify(originalCustomers.value);
  },
  { deep: true }
);

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadCustomers({});
    }
  },
  { immediate: true }
);

async function loadCustomers(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  let sortColumn = params?.column !== undefined ? tableHeaders[params.column]?.value : undefined;

  const queryParams = {
    page: params?.page || customersPageNumber.value,
    itemsPerPage: params?.itemsPerPage || customersItemsPerPage.value,
    query: params?.query || '',
    sortColumn: sortColumn,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    representativeId: props.representativeId,
    isMapped: params?.isMapped,
  };
  const res = await getDCCustomersWithConditions(queryParams);
  if (res) {
    originalCustomers.value = res.data;
    customers.value = deepCopy<DCCustomer[]>(originalCustomers.value);
    customersTotalCount.value = res.count;

    if (params?.page !== undefined) {
      customersPageNumber.value = params.page;
    }
    if (params?.itemsPerPage !== undefined) {
      customersItemsPerPage.value = params.itemsPerPage;
    }
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCCustomer>();

function onDeleteRow(item: DCCustomer) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (deleteRowItem.value && customers.value) {
    const index = customers.value.findIndex((x) => x.id === deleteRowItem.value!.id);
    if (index >= 0) {
      if (customers.value[index].recordMode === RecordMode.Added) {
        customers.value.splice(index, 1);
      } else {
        customers.value[index].recordMode = RecordMode.Deleted;
      }
    }
  }
  modalDeleteRow.value = false;
  deleteRowItem.value = undefined;
}

function onDeleteMultiselect(items: DCCustomer[]) {
  if (!customers.value) return;

  items.forEach((item) => {
    const index = customers.value!.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      if (customers.value![index].recordMode === RecordMode.Added) {
        customers.value!.splice(index, 1);
      } else {
        customers.value![index].recordMode = RecordMode.Deleted;
      }
    }
  });
}

async function onRefreshData(options: TableFilterData) {
  await loadCustomers(options);
}

function initCustomer(): DCCustomer {
  return {
    id: uuidv4(),
    customerId: '',
    representativeId: props.representativeId || '',
    customerRemotedCode: '',
    isActive: true,
    isFromManager: true,
    customer: {
      id: '',
      traderName: '',
      trader: {
        id: '',
        name: '',
        code: '',
      },
    },
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentCustomer.value = initCustomer();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(customer: DCCustomer) {
  currentCustomer.value = deepCopy(customer);
  createEditModalMode.value = props.mode === 'view' ? 'view' : 'edit';
  createEditModalActive.value = true;
}

function onSave(customer: DCCustomer) {
  createEditModalActive.value = false;

  if (createEditModalMode.value === 'add') {
    customers.value.push(customer);
  } else {
    const index = customers.value.findIndex((x) => x.id === customer.id);
    if (index >= 0) {
      if (customers.value[index].recordMode !== RecordMode.Added) {
        customer.recordMode = RecordMode.Modified;
      }
      customers.value[index] = customer;
    }
  }
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  const params = {
    itemsPerPage: 50,
    sortColumn: '',
    sortAscending: true,
    page: 1,
  };

  await onRefreshData(params);
}
</script>
